import { AnimatePresence, motion } from 'framer-motion';
import { MessagesSquare as ChatBubbleLeftRightIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// Create SVG path for donut segments
const createPath = (startAngle: number, endAngle: number, isHovered: boolean = false) => {
  const centerX = 140;
  const centerY = 140;
  const outerRadius = isHovered ? 115 : 110;
  const innerRadius = 70;

  const startAngleRad = (startAngle * Math.PI) / 180;
  const endAngleRad = (endAngle * Math.PI) / 180;

  const x1 = centerX + outerRadius * Math.cos(startAngleRad);
  const y1 = centerY + outerRadius * Math.sin(startAngleRad);
  const x2 = centerX + outerRadius * Math.cos(endAngleRad);
  const y2 = centerY + outerRadius * Math.sin(endAngleRad);

  const x3 = centerX + innerRadius * Math.cos(endAngleRad);
  const y3 = centerY + innerRadius * Math.sin(endAngleRad);
  const x4 = centerX + innerRadius * Math.cos(startAngleRad);
  const y4 = centerY + innerRadius * Math.sin(startAngleRad);

  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4} Z`;
};

interface CaseOriginData {
  [key: string]: number;
}

interface CaseOriginDonutChartProps {
  data: CaseOriginData;
  title: string;
}

// Color mapping for different case origins
const ORIGIN_COLORS = {
  Email: { color: '#3B82F6', gradient: 'emailGradient' },
  Phone: { color: '#10B981', gradient: 'phoneGradient' },
  Chat: { color: '#8B5CF6', gradient: 'chatGradient' },
  'Social Media': { color: '#EC4899', gradient: 'socialGradient' },
  'EHR Onboarding': { color: '#F59E0B', gradient: 'ehrGradient' },
  Web: { color: '#06B6D4', gradient: 'webGradient' },
  Other: { color: '#6B7280', gradient: 'otherGradient' },
  'No Origin': { color: '#9CA3AF', gradient: 'noOriginGradient' },
} as const;

// Default colors for unknown origins
const DEFAULT_COLORS = [
  { color: '#EF4444', gradient: 'defaultGradient1' },
  { color: '#F97316', gradient: 'defaultGradient2' },
  { color: '#84CC16', gradient: 'defaultGradient3' },
  { color: '#06B6D4', gradient: 'defaultGradient4' },
  { color: '#8B5CF6', gradient: 'defaultGradient5' },
  { color: '#EC4899', gradient: 'defaultGradient6' },
];

const CaseOriginDonutChart = ({ data, title }: CaseOriginDonutChartProps) => {
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Function to get color configuration for any origin
  const getOriginColor = (origin: string, index: number) => {
    // Check if we have a predefined color for this origin
    if (origin in ORIGIN_COLORS) {
      return ORIGIN_COLORS[origin as keyof typeof ORIGIN_COLORS];
    }

    // Use default colors for unknown origins
    const defaultColor = DEFAULT_COLORS[index % DEFAULT_COLORS.length];

    return defaultColor;
  };

  const handleMouseEnter = (segment: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    setHoveredSegment(segment);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredSegment(null);
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Calculate total and segments
  const origins = Object.keys(data);
  const total = Object.values(data).reduce((sum, value) => sum + value, 0);

  // Calculate angles for each segment
  const segments = origins.map(origin => {
    const value = data[origin];
    const percentage = total > 0 ? (value / total) * 100 : 0;
    const angle = total > 0 ? (value / total) * 360 : 0;

    return { origin, value, percentage, angle };
  });

  // Create paths for each segment
  let currentAngle = -90; // Start from top
  const segmentPaths = segments.map(segment => {
    const path =
      segment.value > 0
        ? createPath(currentAngle, currentAngle + segment.angle, hoveredSegment === segment.origin)
        : '';
    currentAngle += segment.angle;

    return { ...segment, path };
  });

  // Get tooltip position
  const getTooltipPosition = (origin: string) => {
    const segment = segments.find(s => s.origin === origin);
    if (!segment) return { x: 0, y: 0 };

    const segmentIndex = segments.findIndex(s => s.origin === origin);
    let angle = -90; // Start from top

    for (let i = 0; i < segmentIndex; i++) {
      angle += segments[i].angle;
    }

    angle += segment.angle / 2; // Middle of segment

    const radius = 90;
    const x = 140 + radius * Math.cos((angle * Math.PI) / 180);
    const y = 140 + radius * Math.sin((angle * Math.PI) / 180);

    return { x, y };
  };

  const tooltipPosition = hoveredSegment ? getTooltipPosition(hoveredSegment) : { x: 0, y: 0 };
  const hoveredData = hoveredSegment ? data[hoveredSegment] : 0;
  const hoveredPercentage =
    hoveredSegment && total > 0 ? ((data[hoveredSegment] / total) * 100).toFixed(1) : '0';

  return (
    <motion.div
      className="flex flex-col items-center"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <motion.div
        className="flex items-center justify-center gap-3 mb-6"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        <motion.div
          className="p-2.5 rounded-xl bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 shadow-lg"
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ duration: 0.3, type: 'spring', stiffness: 200 }}
        >
          <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </motion.div>
        <motion.h3
          className="text-xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400 bg-clip-text text-transparent"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          {title}
        </motion.h3>
      </motion.div>

      <div className="relative group z-40 flex justify-center">
        <motion.svg
          width="280"
          height="280"
          viewBox="0 0 280 280"
          className="transform transition-all duration-500 group-hover:scale-105"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, duration: 0.7, ease: 'easeOut' }}
        >
          {/* Gradient definitions */}
          <defs>
            <linearGradient id="emailGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#60A5FA" />
              <stop offset="100%" stopColor="#3B82F6" />
            </linearGradient>
            <linearGradient id="phoneGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#34D399" />
              <stop offset="100%" stopColor="#10B981" />
            </linearGradient>
            <linearGradient id="chatGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#A78BFA" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
            <linearGradient id="socialGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F472B6" />
              <stop offset="100%" stopColor="#EC4899" />
            </linearGradient>
            <linearGradient id="ehrGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FBBF24" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>
            <linearGradient id="webGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22D3EE" />
              <stop offset="100%" stopColor="#06B6D4" />
            </linearGradient>
            <linearGradient id="otherGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#9CA3AF" />
              <stop offset="100%" stopColor="#6B7280" />
            </linearGradient>
            <linearGradient id="noOriginGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D1D5DB" />
              <stop offset="100%" stopColor="#9CA3AF" />
            </linearGradient>
            <linearGradient id="defaultGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F87171" />
              <stop offset="100%" stopColor="#EF4444" />
            </linearGradient>
            <linearGradient id="defaultGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FB923C" />
              <stop offset="100%" stopColor="#F97316" />
            </linearGradient>
            <linearGradient id="defaultGradient3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#A3E635" />
              <stop offset="100%" stopColor="#84CC16" />
            </linearGradient>
            <linearGradient id="defaultGradient4" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22D3EE" />
              <stop offset="100%" stopColor="#06B6D4" />
            </linearGradient>
            <linearGradient id="defaultGradient5" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#A78BFA" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
            <linearGradient id="defaultGradient6" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F472B6" />
              <stop offset="100%" stopColor="#EC4899" />
            </linearGradient>
          </defs>

          {/* Background circle for shadow effect */}
          <circle
            cx="140"
            cy="140"
            r="110"
            fill="none"
            stroke="rgba(0,0,0,0.05)"
            strokeWidth="2"
            className="opacity-50"
          />

          {/* Render segments */}
          {segmentPaths.map((segment, index) => {
            if (segment.value === 0) return null;

            const colorConfig = getOriginColor(segment.origin, index);

            return (
              <motion.path
                key={segment.origin}
                d={segment.path}
                fill={`url(#${colorConfig.gradient})`}
                className="cursor-pointer filter drop-shadow-lg"
                onMouseEnter={() => handleMouseEnter(segment.origin)}
                onMouseLeave={handleMouseLeave}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.5 + index * 0.1, duration: 1.2, ease: 'easeInOut' }}
                whileHover={{
                  filter: `brightness(1.1) drop-shadow(0 8px 16px ${colorConfig.color}30)`,
                  transition: { duration: 0.2 },
                }}
              />
            );
          })}

          {/* Center text */}
          <motion.text
            x="140"
            y="130"
            textAnchor="middle"
            className="text-2xl font-bold fill-gray-900 dark:fill-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
          >
            {total.toLocaleString()}
          </motion.text>
          <motion.text
            x="140"
            y="150"
            textAnchor="middle"
            className="text-sm fill-gray-600 dark:fill-gray-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.5 }}
          >
            TOTAL
          </motion.text>
        </motion.svg>

        {/* Tooltip */}
        <AnimatePresence>
          {hoveredSegment && (
            <motion.div
              className="absolute z-50 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-3 py-2 rounded-lg shadow-xl text-sm font-medium pointer-events-none"
              style={{
                left: tooltipPosition.x - 40,
                top: tooltipPosition.y - 40,
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-center">
                <div className="font-semibold">{hoveredSegment}</div>
                <div>{hoveredData.toLocaleString()} cases</div>
                <div className="text-xs opacity-75">{hoveredPercentage}%</div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Compact Enhanced Legend */}
      <motion.div
        className="mt-6 bg-white/60 dark:bg-gray-800/60 rounded-xl p-4 border border-gray-200/60 dark:border-gray-700/60 shadow-lg backdrop-blur-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.5 }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {segments.map((segment, index) => {
            if (segment.value === 0) return null;

            const colorConfig = getOriginColor(segment.origin, index);

            return (
              <motion.div
                key={segment.origin}
                className="group flex items-center space-x-3 p-3 bg-white/80 dark:bg-gray-800/80 rounded-lg border border-gray-100/80 dark:border-gray-700/80 hover:border-blue-200 dark:hover:border-blue-600/50 hover:shadow-md transition-all duration-200"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.6 + index * 0.1, duration: 0.3 }}
                whileHover={{ scale: 1.02 }}
              >
                {/* Color Indicator with Glow */}
                <div className="relative flex-shrink-0">
                  <div
                    className="w-3.5 h-3.5 rounded-full shadow-md border border-white/80 dark:border-gray-600/80"
                    style={{ backgroundColor: colorConfig.color }}
                  />
                  <div
                    className="absolute inset-0 w-3.5 h-3.5 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-200 blur-sm"
                    style={{ backgroundColor: colorConfig.color }}
                  />
                </div>

                {/* Content */}
                <div className="flex flex-col items-start min-w-0 flex-1">
                  <span className="text-gray-900 dark:text-gray-100 font-medium text-sm leading-tight truncate w-full">
                    {segment.origin}
                  </span>
                  <div className="flex items-center space-x-1.5 mt-0.5">
                    <span className="text-gray-700 dark:text-gray-300 text-xs font-semibold">
                      {segment.value.toLocaleString()}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">cases</span>
                    <span className="text-blue-600 dark:text-blue-400 text-xs font-semibold">
                      ({segment.percentage.toFixed(1)}%)
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CaseOriginDonutChart;
